import { AuthService, ICommunityService } from '@tokens/injection.tokens';
import { Batch, FirebaseBatches } from '@models/downloads/download.model';
import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  DataSnapshot,
  DatabaseReference,
  ref as firebaseRef,
  getDatabase,
  onValue,
} from 'firebase/database';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { map, orderBy } from 'lodash';
import { CommonComponent } from '@components/abstract/common-component.component';
import { CommonVerbsApiService } from '@services/common/common-verbs-api.service';
import { Community } from '@models/communities/community.model';
import { Database } from '@angular/fire/database';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { User } from '@models/account/user.model';
import { environment } from '@environments/environment';
import moment from 'moment';
import { remove } from 'firebase/database';

@Component({
  selector: 'app-global-downloads',
  templateUrl: './global-downloads.component.html',
  styleUrls: ['./global-downloads.component.scss'],
})
export class GlobalDownloadsComponent
  extends CommonComponent
  implements OnInit
{
  protected modal = inject(NgbModal);
  protected router = inject(Router);
  protected api = inject(CommonVerbsApiService);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);

  database = inject(Database);
  mm = moment;

  user$ = inject(AuthService);
  user!: User;
  community$ = inject(ICommunityService);
  community!: Community;
  //
  downloads: Batch[] = [];
  ready = 0;
  total = 0;
  loading = false;
  //
  @ViewChild('dropdownDownloads') dropdown!: NgbDropdown;
  @ViewChild('batchModal', { static: true }) batchModal!: TemplateRef<any>;
  @ViewChild('allModal', { static: true }) allModal!: TemplateRef<any>;

  //
  ngOnInit(): void {
    const subscribe: Subscription = combineLatest([
      this.community$.model$,
      this.user$.model$,
    ])
      .pipe(
        filter(([community, user]) => !!community && !!user),
        distinctUntilChanged(
          ([prevComm, prevUser], [currComm, currUser]) =>
            prevComm?.id === currComm?.id && prevUser?.id === currUser?.id
        ),
        tap(([community, user]) => {
          this.community = community!;
          this.user = user!;
          this.fetch();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
    this.unsubscribe.push(subscribe);
  }
  //
  subscribe: Subscription;

  fetch() {
    const db = getDatabase();
    const path = `${environment.batch}/${this.user!.id}`;
    const reference = firebaseRef(db, path);

    if (this.subscribe) this.subscribe.unsubscribe();
    this.subscribe = this.listenToRealtime<FirebaseBatches>(reference)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.load(data);
        },
        complete: () => this.subscribe.unsubscribe(),
        error: err => {
          //console.error('Firebase error:', err);
          this.toastr.error('Error loading downloads');
        },
      });
  }

  viewAll() {
    const path = `communities/${this.community$.model?.id}/downloads`;
    this.router.navigate([path]).then();
    this.dropdown.close();
  }

  selectedBatch!: Batch;

  onRemove(batch: Batch) {
    if (batch.isBatchInd) {
      this.selectedBatch = batch;
      this.modal.open(this.batchModal, {
        backdrop: 'static',
        centered: true,
        size: 'md',
      });
    } else {
      this.onRemoveApi(batch);
    }
  }

  onRemoveApi(batch: Batch) {
    this.api.delete(`request-download/${batch.id}`).subscribe({
      next: () => {
        this.toastr.success('Changes applied.');
        this.fetch();
      },
      complete: () => this.modal.dismissAll(true),
      error: () => {
        const db = getDatabase();
        const path = `${environment.batch}/${this.user.id}/${batch.key}`;
        const refToDelete = firebaseRef(db, path);

        remove(refToDelete)
          .then(() => {
            this.toastr.success('Changes applied.');
            this.fetch();
            this.modal.dismissAll(true);
          })
          .catch(firebaseErr => {
            this.toastr.error(
              firebaseErr?.message || 'Firebase delete failed.'
            );
          });
      },
    });
  }

  onRemoveAll() {
    this.modal.open(this.allModal, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
  }

  onRemoveAllApi() {
    this.api
      .post(`request-download/batch-remove`, {
        requestDocuments: map(this.downloads, d => d.id),
      })
      .subscribe({
        next: () => {
          this.toastr.success('Changes applied.');
          this.fetch();
        },
        complete: () => this.modal.dismissAll(true),
        error: () => {
          const db = getDatabase();
          const path = `${environment.batch}/${this.user.id}`;
          const refToDelete = firebaseRef(db, path);
          remove(refToDelete)
            .then(() => {
              this.toastr.success('Changes applied.');
              this.fetch();
              this.modal.dismissAll(true);
            })
            .catch(firebaseErr => {
              this.toastr.error(
                firebaseErr?.message || 'Firebase delete failed.'
              );
            });
        },
      });
  }

  private listenToRealtime<T>(ref: DatabaseReference): Observable<T> {
    return new Observable<T>(subscriber => {
      const unsubscribe = onValue(
        ref,
        (snapshot: DataSnapshot) => {
          subscriber.next(snapshot.val());
        },
        error => {
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    });
  }

  download(batch: Batch) {
    if (!batch || batch.disabled) return;

    this.spinner.show().then();

    if (batch.isBatchInd) {
      const path = `communities/${this.community.id}/downloads`;
      this.router
        .navigate([path], { queryParams: { batchInd: 1 } })
        .then(() => {
          this.spinner.hide().then();
          this.dropdown.close();
        });
      return;
    } else {
      if (batch.media) {
        this.api.put(`request-download/${batch.id}/downloaded`).subscribe({
          next: () => {
            const link = document.createElement('a');
            link.href = `${batch.media.url ?? ''}`;
            link.download = batch.media.name!;
            link.target = '_blank';
            link.click();
          },
          error: err => {
            this.toastr.error(err?.error.message || 'An error occurred.');
          },
        });
      }
    }
  }

  private load(data: FirebaseBatches) {
    if (!data) {
      this.downloads = [];
      this.ready = 0;
      this.total = 0;
    } else {
      const downloads = Object.entries(data).map(([key, value]) => {
        this.loading = false;
        const download = {
          ...value,
          id: value.id || key,
          key,
        };

        let extension = '';
        let button = '';
        let buttonClass = '';
        let buttonIcon = '';
        let disabled = true;

        if (download.isBatchInd) {
          const children = download.children || [];
          const total = children.length;
          const complete = children.filter(c => c.status === 'COMPLETE').length;
          const firstChildType = children[0]?.type ?? '';
          extension = firstChildType.includes('_PDF_')
            ? '.pdf'
            : firstChildType.includes('_DOC_')
            ? '.docx'
            : '';

          if (complete === 0) {
            this.loading = true;
            button = `Pending`;
            buttonClass = 'btn-warning';
            buttonIcon = 'bx bx-hourglass bx-spin';
            disabled = true;
          } else if (complete < total) {
            this.loading = true;
            button = `${complete}/${total} Ready`;
            buttonClass = 'btn-warning';
            buttonIcon = 'bx bx-hourglass bx-spin';
            disabled = true;
          } else if (complete === total) {
            button = `${total}/${total} Ready`;
            buttonClass = 'btn-soft-primary';
            buttonIcon = 'bx bx-chevron-right';
            disabled = false;
          }
        } else {
          extension = download.type.includes('_PDF_')
            ? '.pdf'
            : download.type.includes('_DOC_')
            ? '.docx'
            : '';

          switch (download.status) {
            case 'COMPLETE':
              button = 'Download';
              buttonClass = 'btn-primary';
              buttonIcon = 'bx bxs-download';
              disabled = false;
              break;

            case 'DOWNLOADED':
              button = 'Download';
              buttonClass = 'btn-outline-primary';
              buttonIcon = 'bx bxs-check-circle';
              disabled = false;
              break;

            default:
              this.loading = true;
              button = 'Pending';
              buttonClass = 'btn-warning';
              buttonIcon = 'bx bx-hourglass bx-spin';
              disabled = true;
              break;
          }
        }

        return {
          ...download,
          extension,
          button,
          buttonClass,
          buttonIcon,
          disabled,
        };
      });

      this.total = downloads.length;
      this.downloads = orderBy(
        downloads,
        d => new Date(d.createdAt),
        'desc'
      ).slice(0, 5);
      this.ready = this.downloads.filter(
        x => x.status === 'COMPLETE' || x.status === 'DOWNLOADED'
      ).length;
    }
  }
}
